import type { Quadtree } from "d3-quadtree";
import { quadtree } from "d3-quadtree";
import { throttle } from "lodash-es";
import { computed, ref, watch } from "vue";

import { boardActions } from "@/action/boardActions";
import { scrollNearBoarder } from "@/components/utils/scroll";
import { useNativeEvents } from "@/composables/useNativeEvents";
import { relativeClientCoord } from "@/math/coordinate-utils";
import {
  clampCoord,
  insideRectangle,
  maxCoord,
  minCoord,
  minus,
  plus,
  rectangle,
  rectangleSize,
  times,
  toSize,
} from "@/math/coordinates";
import type { BoardCard } from "@/model/card";
import type { Rectangle, RelativeCoordinate } from "@/model/coordinates";
import { relativeCoord } from "@/model/coordinates";
import { halfCardSize, useBoardStore } from "@/store/board";
import { useDrawStore } from "@/store/draw";
import { useModalStore } from "@/store/modal";
import { usePanModeStore } from "@/store/panMode";
import { useSelectionStore } from "@/store/selection";
import { useZoomStore } from "@/store/zoom";
import { clientCoord } from "@/utils/dom/coordinates";
import { isLeftClick } from "@/utils/dom/dom";

export function useMultiSelectRect(width: number, height: number) {
  const { addEventListener, removeEventListeners } = useNativeEvents();

  const start = ref(relativeCoord(0, 0));
  const current = ref(relativeCoord(0, 0));
  const rect = ref<Rectangle<RelativeCoordinate> | null>(null);
  let quadTree: Quadtree<BoardCard> | null = null;

  const isSelecting = computed(() => useSelectionStore().isSelectingWithRect);
  const zooming = computed(() => useZoomStore().zooming);

  const addPadding = (coord: RelativeCoordinate): RelativeCoordinate => {
    const padding = relativeCoord(2 / width, 2 / height);
    return clampCoord(coord, padding, minus(relativeCoord(1, 1), padding));
  };

  const startDrawingRectangle = (event: PointerEvent) => {
    const target = event.target as HTMLElement;
    if (
      target.closest("[data-no-rectangle-select]") ||
      useDrawStore().active ||
      useModalStore().isModalOpen() ||
      zooming.value
    ) {
      return;
    }
    const relativeCoord = addPadding(relativeClientCoord(event));

    start.value = current.value = relativeCoord;
    useSelectionStore().setSelecting("rect");
    // reset selection
    boardActions.clearCardSelection("mouse");

    // Build a QuadTree for efficient spatial queries.
    // This is O(n).
    const { cards } = useBoardStore().currentBoard();
    quadTree = quadtree<BoardCard>()
      .x((card) => card.meta.pos.x)
      .y((card) => card.meta.pos.y)
      .addAll(Object.values(cards));
  };

  const updateRectangle = (event: PointerEvent) => {
    if (!isSelecting.value) return;
    scrollNearBoarder(clientCoord(event));

    current.value = addPadding(relativeClientCoord(event));
    rect.value = rectangle({
      from: minCoord([start.value, current.value]),
      to: maxCoord([start.value, current.value]),
    });

    if (quadTree && rect.value) {
      throttledSelectStickiesInRectWithQuadTree(quadTree, rect.value);
    }
  };

  const finishDrawingRectangle = () => {
    if (isSelecting.value) {
      useSelectionStore().setSelecting("no");

      // Final selection pass via loop in case stickies moved/created by other users,
      // while user was drawing multi-select rect. Otherwise, we would need to recreate or
      // update a QuadTree, which will make things slower.
      if (rect.value) {
        const selectedIds = selectStickiesInRectWithLoop(rect.value);
        boardActions.selectMultipleStickies("mouse", selectedIds);
      }
      rect.value = null;
      quadTree = null;
    }
  };

  const addEventListeners = () => {
    addEventListener(document, "pointerdown", (event: PointerEvent) => {
      if (isLeftClick(event)) startDrawingRectangle(event);
    });
    addEventListener(document, "pointermove", updateRectangle);
    addEventListener(document, "pointerup", finishDrawingRectangle);
  };

  const isMultiSelectRectActive = () => !usePanModeStore().active && !useModalStore().isModalOpen();

  watch(
    isMultiSelectRectActive,
    (isActive) => (isActive ? addEventListeners() : removeEventListeners()),
    { immediate: true },
  );

  const multiSelectRect = computed(() => {
    if (!rect.value) return;
    const from = times(rect.value.p0, width, height);
    const to = times(rectangleSize(rect.value), width, height);
    return { ...from, ...toSize(to) };
  });

  return { multiSelectRect };
}

/**
 * Efficiently selects stickies inside a rectangle using a QuadTree.
 * Time Complexity: O(log n) on average for search.
 */
function selectStickiesInRectWithQuadTree(
  quadTree: Quadtree<BoardCard>,
  rect: Rectangle<RelativeCoordinate>,
) {
  const selectedCardIds = new Array<string>();
  const coverRect = getSelectionBounds(rect);

  quadTree.visit((node, x0, y0, x1, y1) => {
    // Prune branches of the QuadTree that are outside the multi-select rectangle.
    if (x1 < coverRect.p0.x || y1 < coverRect.p0.y || x0 > coverRect.p1.x || y0 > coverRect.p1.y) {
      return true; // terminate this branch visit
    }

    // If node is a leaf (contains a sticky)
    if (!node.length) {
      const card = node.data;
      if (isCardSelectable(card, coverRect)) {
        selectedCardIds.push(card.data.id);
      }
    }
  });

  useBoardStore().setSelectedStickies(selectedCardIds);
}

const throttledSelectStickiesInRectWithQuadTree = throttle(
  (quadTree: Quadtree<BoardCard>, rect: Rectangle<RelativeCoordinate>) => {
    selectStickiesInRectWithQuadTree(quadTree, rect);
  },
  100,
  { leading: true, trailing: true },
);

/**
 * Selects stickies inside a rectangle using a simple loop.
 * Time Complexity: O(n).
 */
function selectStickiesInRectWithLoop(rect: Rectangle<RelativeCoordinate>): string[] {
  const coverRect = getSelectionBounds(rect);
  const { cards } = useBoardStore().currentBoard();

  return Object.keys(cards).filter((id) => isCardSelectable(cards[id], coverRect));
}

function getSelectionBounds(rect: Rectangle<RelativeCoordinate>) {
  return rectangle({ from: minus(rect.p0, halfCardSize()), to: plus(rect.p1, halfCardSize()) });
}

function isCardSelectable(card: BoardCard, coverRect: Rectangle<RelativeCoordinate>) {
  return (
    !card.data.editor &&
    insideRectangle(card.meta.pos, coverRect) &&
    card.meta.mark !== "filter-out"
  );
}
